<template>
  <div>
    <div v-if="schedule.length || editModeActive">
      <hr class="border-grey-divider-subdued my-16" />
      <div class="flex justify-between items-baseline">
        <p class="text-strong-body">General Availability</p>
        <poppins-button
          v-if="editModeActive"
          value="Edit"
          variant="text"
          :href="`/profile/provider/${careType}/${careType}_availability/edit?return_to_profile=true`"
        />
      </div>
    </div>
    <p v-if="!schedule.length && editModeActive" class="text-small text-grey-body">
      Add your typical schedule to show when you’re usually available
    </p>
    <p v-if="showAvailabilityValidUntil" class="text-grey-body text-small">
      Schedule valid until {{ formatInTimeZone(availabilityValidUntil, 'UTC', 'MMMM d, y') }}
    </p>
    <div v-if="emptyAvailability" class="flex flex-col items-center">
      <calendar-up-to-date-illustration />
      <p class="text-grey-body text-small mt-8">Check with {{ sitterName }} to learn more about their schedule</p>
    </div>
    <div v-else-if="schedule.length > 0">
      <div v-for="day in daysOfTheWeek" :key="day" class="flex justify-between mt-16 text-body text-grey-header">
        <p>{{ capitalize(day) }}</p>
        <div class="flex flex-col">
          <div v-for="(dayTime, i) in getTimeRanges(day)" :key="`${day}-${i}`" class="text-small mb-8">
            <p v-if="dayTime === 'Unavailable'">Unavailable</p>
            <div v-else>
              {{ formatTimeTo12Hour(dayTime.startTime) }} – {{ formatTimeTo12Hour(dayTime.endTime) }}
              <p v-if="isOvernight(dayTime.startTime, dayTime.endTime)" class="text-right text-caption">(Overnight)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="hasAvailabilities || editModeActive">
      <hr class="border-grey-divider-subdued my-16" />
      <div class="flex justify-between items-baseline mt-16">
        <p class="text-strong-body">Calendar Availability</p>
        <poppins-button v-if="editModeActive" value="Edit" variant="text" :href="`/availability`" />
      </div>
    </div>
    <p v-if="editModeActive" class="text-small text-grey-body">
      For when you have specific days that deviate from your general schedule
    </p>
    <availability-calendar
      :accountId="sitterId"
      alwaysMobileVariant
      class="mt-24"
      @updateAvailabilities="updateHasAvailabilities"
    />
  </div>
</template>

<script lang="ts">
import capitalize from 'lodash/capitalize';
import { format, parse } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import CalendarUpToDateIllustration from '@/graphics/illustrations/CalendarUpToDateIllustration.vue';
import AvailabilityCalendar from '@/components/calendar/AvailabilityCalendar.vue';
import PoppinsButton from '@/components/button/Button.vue';

export default {
  name: 'ProfileSchedule',
  components: {
    CalendarUpToDateIllustration,
    AvailabilityCalendar,
    PoppinsButton,
  },
  props: {
    schedule: {
      type: Array,
      default: () => [],
    },
    sitterName: {
      type: String,
      default: 'the sitter',
    },
    sitterId: {
      type: String,
      default: '',
    },
    availabilityValidUntil: {
      type: String || null,
      default: null,
    },
    careType: {
      type: String,
      default: '',
    },
    editModeActive: {
      type: Boolean,
      default: false,
    },
  },
  data(): any {
    return {
      hasAvailabilities: false,
      daysOfTheWeek: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'],
    };
  },
  computed: {
    emptyAvailability(): boolean {
      return this.schedule.length === 0 && !this.hasAvailabilities;
    },
    showAvailabilityValidUntil(): boolean {
      return this.availabilityValidUntil && this.careType !== 'babysitter';
    },
  },
  methods: {
    capitalize,
    format,
    formatInTimeZone,
    updateHasAvailabilities(val) {
      this.hasAvailabilities = val;
      this.$emit('has-availability-change', val);
    },
    formatTimeTo12Hour(timeString) {
      const parsedDate = parse(timeString, 'HH:mm', new Date());
      const formattedTime = format(parsedDate, 'h:mm a');
      return formattedTime;
    },
    isOvernight(startTime, endTime) {
      return endTime < startTime;
    },
    getTimeRanges(day) {
      const daySchedule = this.schedule.find((s) => s.day.toUpperCase() === day);
      if (!daySchedule || !daySchedule.timeRanges) {
        return ['Unavailable'];
      }
      return daySchedule.timeRanges;
    },
  },
};
</script>
